// ============================================================
// EsignSettingsPanel.tsx — B4c batch 1a: Admin → E-signature (pilot)
// (ADMINS ONLY — the page and backend /api/esign/settings).
//   • The pilot switch. Off by default; while off nothing about e-signature
//     shows for anyone in the firm (useEsignPilot reads authUser.firmEsignPilot,
//     which this panel updates on save).
//   • Link expiry (3–30 days) and how long evidence is kept (6–50 years, with
//     the server's rule and a "Confirm with a solicitor" note).
//   • The consent-to-sign-electronically wording — the same DRAFT / Approved
//     pattern as Bill / Settlement wording: Insert menu ([Firm name] — stored
//     as the server's {token}), Use Casevo's default, the filled preview,
//     Approve (sends the version read; 409 if it changed) / Withdraw approval.
//     Any edit makes it DRAFT again (the server resets it).
//   • Status: the seal certificate and the locked evidence backup (both must
//     be set up on the server before go-live) and failed integrity checks.
// (batch 1b)
//   • Retention: the client's birthday kept to if they were a child when
//     signing (default 25th — pending a solicitor's confirmation).
//   • Texts and reminders: the text sender name (blank = a short form of the
//     firm name) and the day-3 / day-7 reminders switch.
//   • "How Casevo e-signatures work": the plain-English statement for a court
//     or the other side — read it, Approve (DRAFT until then; a Casevo text
//     change needs a fresh approval) / Withdraw, Download PDF.
//   • Status: whether texts can be sent from this server.
// (E-sign audit fixes A)
//   • "Most signing texts a day" (smsDailyCap, 10–5,000), saved with the
//     text sender name;
//   • switching the pilot OFF asks first — it cancels every open signing
//     link straight away — and then says how many were cancelled.
// (E-sign fixes B)
//   • READ-FIRST cards (the house pattern — ClaimCard / Settlement cards):
//     "Links and records", "Texts" and the consent wording show what's saved;
//     the pencil opens a draft with Cancel / Save in the card's footer bar
//     ("Nothing saves until you press Save"); the unsaved-changes guard while
//     a draft differs. Field errors are FormField + FieldError; refusals are
//     ErrorNote (no more tinted red banners).
//   • The text sender name: 1–11 letters or numbers, no spaces, at least one
//     letter (the server's rule); blank = the default (shown).
//   • The evidence backup job's status: last run, copied, failed, still
//     waiting, the error — or "Not set up".
// The switches (pilot, reminders) still save on their own.
// ============================================================
import React, { useEffect, useRef, useState } from 'react'
import ActionBox, { ErrorNote } from '../shared/ActionBox'
import FormField from '../shared/FormField'
import FieldError from '../shared/FieldError'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import { UnsavedGuard } from '../../hooks/useUnsavedChangesGuard'
import { SettingsCard, SettingsSaved, SettingsSwitch, SettingsToggleRow, S_BTN_GHOST, S_BTN_GHOST_SM, S_BTN_PRIMARY, S_FIELDS, S_FORMFOOT, S_LABEL, S_NOTE, sInput } from './settingsUi'
import { EditFooter, EditPencil } from '../settlement/settlementUi'
import { InsertMenu, insertAt, wordingStatusText, wordingToDisplay, wordingToTokens } from './BillWordingPanel'
import { errorText } from '../billing/billingUi'
import { approveEsignConsentApi, approveEsignExplainerApi, getEsignExplainerPdfApi, getEsignSettingsApi, patchEsignSettingsApi, unapproveEsignConsentApi, unapproveEsignExplainerApi } from '../../api/casevoApi'
import { useCasevoStore } from '../../store/useCasevoStore'
import { ESIGN_CHILD_AGE, ESIGN_CONSENT_LENGTH, ESIGN_LINK_DAYS, ESIGN_RETENTION_YEARS, ESIGN_SMS_DAILY_CAP, SMS_SENDER_NAME_RE, type EsignBackupStatus, type EsignPurgeStatus, type EsignSettings, type EsignSettingsPatch } from '../../types/esign.types'
import { downloadBlob } from '../esign/esignFormat'
import { groupHex, ukDate, ukDateTime } from '../esign/esignFormat'

export const PILOT_HELP = 'Off by default. While it’s off, nothing about e-signature shows for anyone in your firm.'
export const PILOT_OFF_WARNING = 'Switching e-signature off cancels every open signing link straight away. They won’t work again if you switch it back on.'
export const SMS_CAP_HELP = 'The most signing-code texts your firm sends in any 24 hours. Signers see a plain message if it’s reached.'
// "Cancelled 3 open signing links." (+ any that couldn't be marked cancelled).
export const linksRevokedText = (revoked: number, failed = 0): string =>
  `Cancelled ${revoked} open signing link${revoked === 1 ? '' : 's'}.${failed > 0 ? ` ${failed} couldn’t be marked cancelled just now, but no link works while e-signature is off.` : ''}`
export const CONSENT_TITLE = 'Consent to sign electronically — DRAFT until a solicitor approves'

// A whole number within [min, max], else the sentence to show.
export const rangeError = (raw: string, min: number, max: number, unit: string): string | null => {
  const t = raw.trim()
  if (!/^\d+$/.test(t)) return `Enter a whole number of ${unit}, from ${min} to ${max}.`
  const n = Number(t)
  return n < min || n > max ? `Choose between ${min} and ${max} ${unit}.` : null
}

// The firm's wording as typed ([Firm name]) → what's saved: null when it is
// Casevo's default.
export const consentPatchValue = (d: EsignSettings, display: string): string | null => {
  const tokens = wordingToTokens(display.trim(), d.consent.placeholders)
  return tokens === d.consent.defaultWording.trim() ? null : tokens
}
const savedDisplay = (d: EsignSettings): string => wordingToDisplay(d.consent.wording ?? d.consent.defaultWording, d.consent.placeholders)

type CardKey = 'limits' | 'texts' | 'consent'
type Saved = 'pilot' | CardKey | 'reminders' | null
export const EXPLAINER_DRAFT = 'DRAFT — not yet approved by a solicitor at your firm'
// (E-sign fixes B) The server's rule and its words.
export const SENDER_ERROR = 'The text sender name must be 1–11 letters or numbers, with at least one letter.'
export const SENDER_HELP = '1 to 11 letters or numbers, no spaces, with at least one letter.'
export const senderError = (raw: string): string | null =>
  raw.trim() && !SMS_SENDER_NAME_RE.test(raw.trim()) ? SENDER_ERROR : null

const CARD_ID: Record<CardKey, string> = { limits: 'es-limits', texts: 'es-texts', consent: 'es-consent-card' }
const CARD_NAME: Record<CardKey, string> = { limits: 'links and records', texts: 'texts', consent: 'consent wording' }

// ── (E-sign fixes B) The evidence backup job, in plain English ──
const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`
export const backupView = (b: EsignBackupStatus): { tone: 'done' | 'warn' | 'danger'; title: string; message: string } => {
  if (!b.configured) return { tone: 'danger', title: 'Evidence backup not set up', message: 'Signed evidence is only in R2 until the locked S3 copy is set up. This must be fixed before go-live.' }
  const waiting = b.backlog === 0 ? 'Nothing is waiting to be copied.' : `${plural(b.backlog, 'file')} waiting to be copied.`
  const r = b.lastRun
  if (!r) return { tone: 'warn', title: 'Evidence backup hasn’t run yet', message: `It runs automatically. ${waiting}` }
  if (r.error || r.failed > 0) return { tone: 'danger', title: 'The last evidence backup had problems', message: `${r.failed > 0 ? `${plural(r.failed, 'file')} couldn’t be copied. ` : ''}It tries again on the next run. If this keeps happening, contact Casevo support.` }
  return { tone: 'done', title: 'Evidence backup', message: waiting }
}

// ── (D12) The retention purge, in plain English (like the backup) ──
export const REMINDERS_DESC = 'One reminder on day 3 and one on day 7 to anyone who hasn’t signed. Each reminder carries a fresh link — the earlier link stops working once it’s sent.'
export const purgeView = (p: EsignPurgeStatus): { tone: 'done' | 'warn' | 'danger' | 'info'; title: string; message: string } => {
  const due = p.due === 0 ? 'No signing records are past their keep-until date.' : `${plural(p.due, 'signing record')} ${p.due === 1 ? 'is' : 'are'} past ${p.due === 1 ? 'its' : 'their'} keep-until date.`
  const lock = p.waitingForLock > 0 ? ` ${plural(p.waitingForLock, 'record')} ${p.waitingForLock === 1 ? 'is' : 'are'} waiting for the locked backup copy to unlock — they’re removed after that.` : ''
  if (!p.guardInstalled) return { tone: 'danger', title: 'Retention purge can’t run yet', message: `The database safeguard for the purge isn’t installed, so nothing is removed. Casevo must install it on the server. ${due}` }
  const r = p.lastRun
  if (!r) return { tone: p.due > 0 ? 'warn' : 'info', title: 'Retention purge hasn’t run yet', message: `It runs automatically. ${due}${lock}` }
  if (r.error || r.failed > 0) return { tone: 'danger', title: 'The last retention purge had problems', message: `${r.failed > 0 ? `${plural(r.failed, 'record')} couldn’t be purged. ` : ''}It tries again on the next run. If this keeps happening, contact Casevo support.` }
  return { tone: 'done', title: 'Retention purge', message: `${due}${lock}` }
}

const ReadRow: React.FC<{ label: string; children: React.ReactNode; note?: React.ReactNode }> = ({ label, children, note }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)] gap-x-[16px] gap-y-[2px] py-[10px] border-t border-casevo-border-soft first:border-t-0">
    <dt className="text-[13px] text-casevo-text-soft">{label}</dt>
    <dd className="m-0 min-w-0 text-[14px] font-medium text-casevo-forest break-words">
      {children}
      {note && <span className="block font-normal text-[12.5px] text-casevo-text-soft mt-[2px] leading-[1.45]">{note}</span>}
    </dd>
  </div>
)

const EsignSettingsPanel: React.FC = () => {
  const [data, setData] = useState<EsignSettings | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  // Which read-first cards are open (each has its own draft).
  const [open, setOpen] = useState<Record<CardKey, boolean>>({ limits: false, texts: false, consent: false })
  const [tried, setTried] = useState<Record<CardKey, boolean>>({ limits: false, texts: false, consent: false })
  const [linkDays, setLinkDays] = useState('')
  const [retention, setRetention] = useState('')
  const [childAge, setChildAge] = useState('')
  const [sender, setSender] = useState('')
  const [cap, setCap] = useState('')
  // (audit fixes A) Switching the pilot off: asked first; then how many links were cancelled.
  const [confirmPilotOff, setConfirmPilotOff] = useState(false)
  const [revokedNote, setRevokedNote] = useState<string | null>(null)
  const [consent, setConsent] = useState('')
  const [error, setError] = useState<{ where: Exclude<Saved, null> | 'approval' | 'explainer'; message: string } | null>(null)
  const [busy, setBusy] = useState<Saved | 'approval' | 'explainer' | 'explainerPdf'>(null)
  const [confirmExplainerWithdraw, setConfirmExplainerWithdraw] = useState(false)
  const [saved, setSaved] = useState<Saved>(null)
  const [confirmWithdraw, setConfirmWithdraw] = useState(false)
  const consentRef = useRef<HTMLTextAreaElement | null>(null)

  const seed = (d: EsignSettings, parts: { limits?: boolean; consent?: boolean; texts?: boolean } = { limits: true, consent: true, texts: true }): void => {
    setData(d)
    if (parts.limits) { setLinkDays(String(d.linkDays)); setRetention(String(d.retentionYears)); setChildAge(String(d.retentionChildAge)) }
    if (parts.texts) { setSender(d.smsSenderName ?? ''); setCap(String(d.smsDailyCap)) }
    if (parts.consent) setConsent(savedDisplay(d))
    // Keep the signed-in user's pilot flag in step (the rest of the app reads it).
    const st = useCasevoStore.getState()
    const u = st.authUser
    if (u && u.firmEsignPilot !== d.pilotEnabled) st.setAuthUser({ ...u, firmEsignPilot: d.pilotEnabled })
  }
  useEffect(() => {
    getEsignSettingsApi().then((d) => seed(d)).catch((e) => setLoadErr(errorText(e, 'Couldn’t load the e-signature settings.')))
  }, [])

  if (loadErr) return <ErrorNote>{loadErr}</ErrorNote>
  if (!data) return <p className={S_NOTE}>Loading the e-signature settings…</p>

  const limitsDirty = open.limits && (linkDays.trim() !== String(data.linkDays) || retention.trim() !== String(data.retentionYears) || childAge.trim() !== String(data.retentionChildAge))
  const textsDirty = open.texts && (sender.trim() !== (data.smsSenderName ?? '') || cap.trim() !== String(data.smsDailyCap))
  const consentValue = consentPatchValue(data, consent)
  const consentDirty = open.consent && consentValue !== data.consent.wording
  const usingDefault = consentValue === null

  // The pencil of a card (focus goes back to it on Save / Cancel).
  const backToPencil = (card: CardKey): void => {
    window.setTimeout(() => document.querySelector<HTMLElement>(`#${CARD_ID[card]} [data-edit-pencil]`)?.focus(), 0)
  }
  const openCard = (card: CardKey): void => {
    setOpen((o) => ({ ...o, [card]: true })); setTried((t) => ({ ...t, [card]: false })); setSaved(null)
    if (error?.where === card) setError(null)
    if (card === 'limits') { setLinkDays(String(data.linkDays)); setRetention(String(data.retentionYears)); setChildAge(String(data.retentionChildAge)) }
    if (card === 'texts') { setSender(data.smsSenderName ?? ''); setCap(String(data.smsDailyCap)) }
    if (card === 'consent') { setConsent(savedDisplay(data)) }
  }
  const closeCard = (card: CardKey): void => {
    setOpen((o) => ({ ...o, [card]: false })); setTried((t) => ({ ...t, [card]: false }))
    if (error?.where === card) setError(null)
    backToPencil(card)
  }

  const patch = async (where: Exclude<Saved, null>, body: EsignSettingsPatch, parts: { limits?: boolean; consent?: boolean; texts?: boolean }): Promise<boolean> => {
    setBusy(where); setError(null); setSaved(null); setRevokedNote(null)
    try {
      const r = await patchEsignSettingsApi(body)
      seed(r, parts); setSaved(where)
      if (typeof r.linksRevoked === 'number') setRevokedNote(linksRevokedText(r.linksRevoked, r.revokeFailed ?? 0))
      return true
    } catch (e) {
      setError({ where, message: errorText(e, 'Couldn’t save — please try again.') })
      return false
    } finally { setBusy(null) }
  }
  // Save a card: nothing changed → just close it.
  const saveCard = async (card: CardKey, body: EsignSettingsPatch): Promise<void> => {
    if (Object.keys(body).length === 0) { closeCard(card); return }
    if (await patch(card, body, { [card]: true })) { setOpen((o) => ({ ...o, [card]: false })); setTried((t) => ({ ...t, [card]: false })); backToPencil(card) }
  }
  // (audit fixes A) Off cancels every open link — ask first.
  const togglePilot = (): void => { if (data.pilotEnabled) setConfirmPilotOff(true); else void patch('pilot', { pilotEnabled: true }, {}) }
  const limitsErrs = {
    linkDays: rangeError(linkDays, ESIGN_LINK_DAYS.min, ESIGN_LINK_DAYS.max, 'days') ?? undefined,
    retentionYears: rangeError(retention, ESIGN_RETENTION_YEARS.min, ESIGN_RETENTION_YEARS.max, 'years') ?? undefined,
    childAge: rangeError(childAge, ESIGN_CHILD_AGE.min, ESIGN_CHILD_AGE.max, 'years old') ?? undefined,
  }
  const saveLimits = (): void => {
    setTried((t) => ({ ...t, limits: true }))
    if (limitsErrs.linkDays || limitsErrs.retentionYears || limitsErrs.childAge) return
    const body: EsignSettingsPatch = {}
    if (Number(linkDays) !== data.linkDays) body.linkDays = Number(linkDays)
    if (Number(retention) !== data.retentionYears) body.retentionYears = Number(retention)
    if (Number(childAge) !== data.retentionChildAge) body.retentionChildAge = Number(childAge)
    void saveCard('limits', body)
  }
  const consentLenErr = ((): string | undefined => {
    const len = consentValue === null ? ESIGN_CONSENT_LENGTH.min : consentValue.length
    return len < ESIGN_CONSENT_LENGTH.min || len > ESIGN_CONSENT_LENGTH.max ? `The wording must be between ${ESIGN_CONSENT_LENGTH.min} and ${ESIGN_CONSENT_LENGTH.max} characters.` : undefined
  })()
  const saveConsent = (): void => {
    setTried((t) => ({ ...t, consent: true }))
    if (consentLenErr) return
    void saveCard('consent', consentValue !== data.consent.wording ? { consentWording: consentValue } : {})
  }
  const approve = async (yes: boolean): Promise<void> => {
    setBusy('approval'); setError(null); setSaved(null)
    try { seed(yes ? await approveEsignConsentApi(data.consent.version) : await unapproveEsignConsentApi(), { consent: !consentDirty }) } catch (e) {
      setError({ where: 'approval', message: errorText(e, 'Couldn’t change the approval.') })
      // The wording changed since it was read → show the current version.
      if ((e as { response?: { status?: number } }).response?.status === 409) getEsignSettingsApi().then((d) => seed(d, { consent: !consentDirty })).catch(() => undefined)
    } finally { setBusy(null) }
  }
  const textsErrs = { sender: senderError(sender) ?? undefined, cap: rangeError(cap, ESIGN_SMS_DAILY_CAP.min, ESIGN_SMS_DAILY_CAP.max, 'texts') ?? undefined }
  const saveTexts = (): void => {
    setTried((t) => ({ ...t, texts: true }))
    if (textsErrs.sender || textsErrs.cap) return
    const body: EsignSettingsPatch = {}
    if (sender.trim() !== (data.smsSenderName ?? '')) body.smsSenderName = sender.trim() || null
    if (Number(cap) !== data.smsDailyCap) body.smsDailyCap = Number(cap)
    void saveCard('texts', body)
  }
  const approveExplainer = async (yes: boolean): Promise<void> => {
    setBusy('explainer'); setError(null); setSaved(null)
    try { seed(yes ? await approveEsignExplainerApi(data.explainer.version) : await unapproveEsignExplainerApi(), {}) } catch (e) {
      setError({ where: 'explainer', message: errorText(e, 'Couldn’t change the approval.') })
    } finally { setBusy(null) }
  }
  const downloadExplainer = async (): Promise<void> => {
    setBusy('explainerPdf'); setError(null)
    try { downloadBlob(await getEsignExplainerPdfApi(), `How Casevo e-signatures work${data.explainer.approved ? '' : ' (DRAFT)'}.pdf`) } catch (e) {
      setError({ where: 'explainer', message: errorText(e, 'Couldn’t make the PDF.') })
    } finally { setBusy(null) }
  }
  const errAt = (where: Exclude<Saved, null> | 'approval' | 'explainer'): React.ReactNode =>
    error?.where === where ? <ErrorNote className="mt-[12px]">{error.message}</ErrorNote> : null

  // The header's right side: "Saved" after a save, the pencil (read view) or "Editing".
  const cardRight = (card: CardKey): React.ReactNode => open[card]
    ? <span className="text-[12.5px] text-casevo-text-soft">Editing</span>
    : <>{saved === card && <SettingsSaved />}<EditPencil title={CARD_NAME[card]} onClick={() => openCard(card)} /></>
  const footer = (card: CardKey, blocked: boolean, onSave: () => void): React.ReactNode => open[card]
    ? <EditFooter blocked={blocked} saving={busy === card} onCancel={() => closeCard(card)} onSave={onSave} />
    : null
  // A field's error shows once Save was pressed, and follows the value from then on.
  const liveErr: Record<string, string | undefined> = { ...limitsErrs, ...textsErrs, consent: consentLenErr }
  const fieldErrShown = (k: string, card: CardKey): string | undefined => (tried[card] ? liveErr[k] : undefined)

  const { seal, evidence } = data.status
  const sealExpired = seal.configured && new Date(seal.validUntil).getTime() < Date.now()
  const backup = data.backup
  const bv = backup ? backupView(backup) : null
  // (D12) The retention purge (undefined = an older server; null = couldn't be read).
  const purge = data.purge
  const pv = purge ? purgeView(purge) : null

  return (
    <div className="space-y-[18px]">
      <UnsavedGuard dirty={(consentDirty || limitsDirty || textsDirty) && busy === null} />
      {/* ── The pilot switch ── */}
      <SettingsCard icon="pen" title="E-signature (pilot)" sub="Clients and others sign documents online; Casevo seals the signed PDF and keeps the evidence.">
        <SettingsToggleRow title="E-signature (pilot)" desc={PILOT_HELP}
          control={<SettingsSwitch checked={data.pilotEnabled} onChange={togglePilot} label="E-signature (pilot)" disabled={busy === 'pilot'} />} />
        {errAt('pilot')}
        {saved === 'pilot' && <div className="mt-[4px]"><SettingsSaved>{data.pilotEnabled ? 'Saved — e-signature is on for your firm' : 'Saved — e-signature is off for your firm'}</SettingsSaved></div>}
        {revokedNote && <ActionBox live variant="slim" tone="info" title={revokedNote} className="mt-[6px]" />}
      </SettingsCard>

      {/* ── Link expiry + retention (read-first) ── */}
      <SettingsCard id={CARD_ID.limits} icon="cal" title="Links and records" sub="How long a signing link works, and how long the signed evidence is kept."
        right={cardRight('limits')} editing={open.limits}
        footer={footer('limits', tried.limits && !!(limitsErrs.linkDays || limitsErrs.retentionYears || limitsErrs.childAge), saveLimits)}>
        {open.limits ? (
          <div className={S_FIELDS}>
            <FormField label="Link expiry (days)" htmlFor="es-link-days">
              <input id="es-link-days" type="number" inputMode="numeric" min={ESIGN_LINK_DAYS.min} max={ESIGN_LINK_DAYS.max} value={linkDays}
                aria-invalid={fieldErrShown('linkDays', 'limits') ? true : undefined} aria-describedby="es-link-days-err es-link-days-note"
                onChange={(e) => setLinkDays(e.target.value)}
                className={sInput(!!fieldErrShown('linkDays', 'limits'))} />
              <span id="es-link-days-err"><FieldError message={fieldErrShown('linkDays', 'limits')} /></span>
              <p id="es-link-days-note" className={`${S_NOTE} mt-[5px]`}>From {ESIGN_LINK_DAYS.min} to {ESIGN_LINK_DAYS.max} days. A signer whose link has expired asks you for a new one.</p>
            </FormField>
            <FormField label="Keep evidence for (years)" htmlFor="es-retention">
              <input id="es-retention" type="number" inputMode="numeric" min={ESIGN_RETENTION_YEARS.min} max={ESIGN_RETENTION_YEARS.max} value={retention}
                aria-invalid={fieldErrShown('retentionYears', 'limits') ? true : undefined} aria-describedby="es-retention-err es-retention-rule"
                onChange={(e) => setRetention(e.target.value)}
                className={sInput(!!fieldErrShown('retentionYears', 'limits'))} />
              <span id="es-retention-err"><FieldError message={fieldErrShown('retentionYears', 'limits')} /></span>
              <p id="es-retention-rule" className={`${S_NOTE} mt-[5px]`}>From {ESIGN_RETENTION_YEARS.min} to {ESIGN_RETENTION_YEARS.max} years after the case closes.</p>
            </FormField>
            <FormField label="A child client: keep until their birthday" htmlFor="es-child-age">
              <input id="es-child-age" type="number" inputMode="numeric" min={ESIGN_CHILD_AGE.min} max={ESIGN_CHILD_AGE.max} value={childAge}
                aria-invalid={fieldErrShown('childAge', 'limits') ? true : undefined} aria-describedby="es-child-age-err es-child-age-note"
                onChange={(e) => setChildAge(e.target.value)}
                className={sInput(!!fieldErrShown('childAge', 'limits'))} />
              <span id="es-child-age-err"><FieldError message={fieldErrShown('childAge', 'limits')} /></span>
              <p id="es-child-age-note" className={`${S_NOTE} mt-[5px]`}>If the client was under 18 when they signed — their {childAge.trim() || data.retentionChildAge}th birthday, when that is later.</p>
            </FormField>
          </div>
        ) : (
          <dl className="m-0" data-read-view="limits">
            <ReadRow label="Link expiry">{plural(data.linkDays, 'day')}</ReadRow>
            <ReadRow label="Keep evidence for">{plural(data.retentionYears, 'year')} after the case closes</ReadRow>
            <ReadRow label="A child client">Until their {data.retentionChildAge}th birthday, if that is later</ReadRow>
          </dl>
        )}
        <p className={`${S_NOTE} mt-[6px]`} data-retention-rule="">{data.retentionRule}</p>
        <ActionBox variant="slim" tone="warn" title={data.retentionNote.replace(/\.$/, '')} message="Check the retention period against your firm’s policy before go-live." className="mt-[6px]" />
        {errAt('limits')}
      </SettingsCard>

      {/* ── (batch 1b) Texts and reminders (read-first; the reminders switch saves on its own) ── */}
      <SettingsCard id={CARD_ID.texts} icon="send" title="Texts and reminders" sub="The signing code is texted from this name. Reminders go by email on day 3 and day 7 if a document isn’t signed."
        right={cardRight('texts')} editing={open.texts}
        footer={footer('texts', tried.texts && !!(textsErrs.sender || textsErrs.cap), saveTexts)}>
        {open.texts ? (
          <div className={S_FIELDS}>
            <FormField label="Text sender name" htmlFor="es-sender">
              <input id="es-sender" value={sender} maxLength={11} placeholder={data.smsSenderShown} autoComplete="off" spellCheck={false}
                aria-invalid={fieldErrShown('sender', 'texts') ? true : undefined} aria-describedby="es-sender-err es-sender-note"
                onChange={(e) => setSender(e.target.value)}
                className={sInput(!!fieldErrShown('sender', 'texts'))} />
              <span id="es-sender-err"><FieldError message={fieldErrShown('sender', 'texts')} /></span>
              <p id="es-sender-note" className={`${S_NOTE} mt-[5px]`}>{SENDER_HELP} Leave blank to use “{data.smsSenderShown}”. Texts never contain the link or any case details.</p>
            </FormField>
            <FormField label="Most signing texts a day" htmlFor="es-sms-cap">
              <input id="es-sms-cap" type="number" inputMode="numeric" min={ESIGN_SMS_DAILY_CAP.min} max={ESIGN_SMS_DAILY_CAP.max} value={cap}
                aria-invalid={fieldErrShown('cap', 'texts') ? true : undefined} aria-describedby="es-sms-cap-err es-sms-cap-note"
                onChange={(e) => setCap(e.target.value)}
                className={sInput(!!fieldErrShown('cap', 'texts'))} />
              <span id="es-sms-cap-err"><FieldError message={fieldErrShown('cap', 'texts')} /></span>
              <p id="es-sms-cap-note" className={`${S_NOTE} mt-[5px]`}>{SMS_CAP_HELP} From {ESIGN_SMS_DAILY_CAP.min} to {ESIGN_SMS_DAILY_CAP.max.toLocaleString('en-GB')}.</p>
            </FormField>
          </div>
        ) : (
          <dl className="m-0" data-read-view="texts">
            <ReadRow label="Text sender name" note={data.smsSenderName ? undefined : 'The default — a short form of your firm’s name.'}>
              <span data-testid="es-sender-shown">{data.smsSenderName ?? data.smsSenderShown}</span>
            </ReadRow>
            <ReadRow label="Most signing texts a day" note={SMS_CAP_HELP}>{data.smsDailyCap.toLocaleString('en-GB')}</ReadRow>
          </dl>
        )}
        {errAt('texts')}
        <div className="mt-[6px] border-t border-casevo-border-soft">
          <SettingsToggleRow title="Send reminders" desc={REMINDERS_DESC}
            control={<SettingsSwitch checked={data.remindersEnabled} onChange={() => { void patch('reminders', { remindersEnabled: !data.remindersEnabled }, {}) }} label="Send reminders" disabled={busy === 'reminders'} />} />
        </div>
        {errAt('reminders')}
        {saved === 'reminders' && <div className="mt-[4px]"><SettingsSaved>{data.remindersEnabled ? 'Saved — reminders are on' : 'Saved — reminders are off'}</SettingsSaved></div>}
      </SettingsCard>

      {/* ── Consent wording (read-first; approve from the read view) ── */}
      <SettingsCard id={CARD_ID.consent} icon="check" title={CONSENT_TITLE} sub="Shown to every signer before they sign. Casevo’s default text is DRAFT and must be reviewed by a solicitor at your firm."
        right={cardRight('consent')} editing={open.consent}
        footer={footer('consent', tried.consent && !!consentLenErr, saveConsent)}>
        <ActionBox live tone={data.consent.approved ? 'done' : 'danger'} title={wordingStatusText(data.consent)}
          message={data.consent.approved ? undefined : 'Documents can’t be sent for e-signature until a solicitor at your firm approves this wording.'} />
        {errAt('approval')}
        {!open.consent && (
          <>
            <div className={S_FORMFOOT}>
              {data.consent.approved
                ? <button type="button" className={S_BTN_GHOST} disabled={busy === 'approval'} onClick={() => setConfirmWithdraw(true)}>Withdraw approval</button>
                : <button type="button" className={S_BTN_PRIMARY} disabled={busy === 'approval'} onClick={() => { void approve(true) }}>Approve wording</button>}
            </div>
            <p className={`${S_LABEL} mt-[18px]`}>As signers see it</p>
            <p id="es-consent-preview" className="m-0 rounded-[10px] border border-casevo-border-soft bg-casevo-input px-4 py-[10px] text-[13.5px] leading-[1.55] text-casevo-forest" data-consent-preview="">{data.consent.preview}</p>
            {data.consent.wording === null && <p className={`${S_NOTE} mt-[6px]`}>Casevo’s default wording.</p>}
          </>
        )}
        {open.consent && (
          <>
            <div className="flex items-center gap-[8px] mt-[18px] flex-wrap">
              <label htmlFor="es-consent" className={`${S_LABEL} mb-0`}>Consent wording</label>
              <div className="ml-auto flex items-center gap-[6px]">
                <InsertMenu label="Consent wording" placeholders={data.consent.placeholders} onPick={(l) => {
                  const el = consentRef.current
                  const r = insertAt(consent, `[${l}]`, el ? el.selectionStart : null, el ? el.selectionEnd : null)
                  setConsent(r.text)
                  requestAnimationFrame(() => { if (el) { el.focus(); el.setSelectionRange(r.caret, r.caret) } })
                }} />
                {!usingDefault && (
                  <button type="button" className={S_BTN_GHOST_SM} onClick={() => { setConsent(wordingToDisplay(data.consent.defaultWording, data.consent.placeholders)) }}>Use Casevo’s default</button>
                )}
              </div>
            </div>
            <textarea id="es-consent" ref={consentRef} rows={6} value={consent}
              onChange={(e) => setConsent(e.target.value)}
              aria-invalid={fieldErrShown('consent', 'consent') ? true : undefined} aria-describedby="es-consent-err es-consent-details es-consent-preview"
              className={`${sInput(!!fieldErrShown('consent', 'consent'))} !h-auto py-[8px] mt-[6px] resize-y leading-[1.5]`} />
            <span id="es-consent-err"><FieldError message={fieldErrShown('consent', 'consent')} /></span>
            {data.consent.placeholders.length > 0 && (
              <p id="es-consent-details" className={`${S_NOTE} mt-[6px]`}>
                Filled in for each signer: {data.consent.placeholders.map((p) => `[${p.label}]`).join(', ')} — add them from Insert.
              </p>
            )}
            <p id="es-consent-preview" className={`${S_NOTE} mt-[6px]`}><b className="font-semibold text-casevo-forest">As signers see it (saved version):</b> {data.consent.preview}</p>
            {data.consent.approved && <p className={`${S_NOTE} mt-[6px]`}>Saving a change makes the wording DRAFT again until it’s re-approved.</p>}
          </>
        )}
        {errAt('consent')}
      </SettingsCard>

      {/* ── (batch 1b) How Casevo e-signatures work ── */}
      <SettingsCard id="es-explainer" icon="list" title={data.explainer.title} sub="A plain-English statement you can give a court, the other side or an auditor. It goes in every evidence pack.">
        <ActionBox live tone={data.explainer.approved ? 'done' : 'warn'}
          title={data.explainer.approved ? `Approved by ${data.explainer.approvedBy}${data.explainer.approvedAt ? ` on ${ukDate(data.explainer.approvedAt)}` : ''}` : EXPLAINER_DRAFT}
          message={data.explainer.approved ? undefined : data.explainer.outdated ? 'Casevo has updated this statement since it was last approved. Read it again and approve this version.' : 'Until it’s approved, the PDF and the evidence packs mark it DRAFT.'} />
        {errAt('explainer')}
        <div className={S_FORMFOOT}>
          {data.explainer.approved
            ? <button type="button" className={S_BTN_GHOST} disabled={busy === 'explainer'} onClick={() => setConfirmExplainerWithdraw(true)}>Withdraw approval</button>
            : <button type="button" className={S_BTN_PRIMARY} disabled={busy === 'explainer'} onClick={() => { void approveExplainer(true) }}>Approve this statement</button>}
          <button type="button" className={S_BTN_GHOST} disabled={busy === 'explainerPdf'} onClick={() => { void downloadExplainer() }}>{busy === 'explainerPdf' ? 'Making the PDF…' : 'Download PDF'}</button>
          <span className={S_NOTE}>Version {data.explainer.version}</span>
        </div>
        <div className="mt-[14px] max-h-[420px] overflow-y-auto rounded-[10px] border border-casevo-border-soft bg-casevo-input px-4 py-[10px]" tabIndex={0} aria-label="The statement" data-explainer-text="">
          {data.explainer.sections.map((sec) => (
            <section key={sec.heading} className="mb-[10px]">
              <h4 className="m-0 mb-[4px] text-[13px] font-semibold text-casevo-forest">{sec.heading}</h4>
              {sec.paragraphs.map((para, i) => <p key={i} className="m-0 mb-[6px] text-[12.5px] leading-[1.55] text-casevo-forest">{para}</p>)}
            </section>
          ))}
        </div>
      </SettingsCard>

      {/* ── Server status ── */}
      <SettingsCard id="es-status" icon="shield" title="Seal and evidence" sub="Set up on the server by Casevo. Both must be in place before go-live.">
        <div className="space-y-[12px]">
          {seal.configured ? (
            <ActionBox variant="slim" tone={sealExpired ? 'danger' : 'done'} title={sealExpired ? 'Seal certificate expired' : 'Seal certificate'} status={sealExpired ? undefined : '· configured'} ariaLabel="Seal certificate">
              <dl className="m-0 pl-[54px] pr-4 pb-[12px] -mt-[2px] grid grid-cols-1 sm:grid-cols-[120px_minmax(0,1fr)] gap-x-[12px] gap-y-[2px] sm:gap-y-[6px] text-[12.5px]">
                <dt className="text-casevo-text-soft">Subject</dt><dd className="m-0 mb-[6px] sm:mb-0 text-casevo-forest break-words">{seal.subject}</dd>
                <dt className="text-casevo-text-soft">Fingerprint</dt><dd className="m-0 mb-[6px] sm:mb-0 font-mono text-[11.5px] leading-[1.5] text-casevo-forest break-words">{groupHex(seal.fingerprint)}</dd>
                <dt className="text-casevo-text-soft">Valid until</dt><dd className="m-0 text-casevo-forest">{ukDate(seal.validUntil)}</dd>
              </dl>
            </ActionBox>
          ) : (
            <ActionBox tone="danger" title="Seal certificate not configured"
              message="Signed documents can’t be sealed until ESIGN_SEAL_P12_BASE64 and ESIGN_SEAL_P12_PASSWORD are set on the server." />
          )}
          {/* (E-sign fixes B) The backup job's own status when the server sends it. */}
          {backup && bv ? (
            <ActionBox tone={bv.tone} title={bv.title} message={bv.message} ariaLabel={bv.title}>
              {backup.configured && backup.lastRun && (
                <dl className="m-0 mt-[8px] grid grid-cols-1 sm:grid-cols-[130px_minmax(0,1fr)] gap-x-[12px] gap-y-[2px] sm:gap-y-[4px] text-[12.5px]" data-backup-run="">
                  <dt className="text-casevo-text-soft">Last run</dt>
                  <dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest">{ukDateTime(backup.lastRun.startedAt)}{backup.lastRun.finishedAt ? '' : ' (still running)'}</dd>
                  <dt className="text-casevo-text-soft">Copied</dt><dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest tabular-nums">{plural(backup.lastRun.copied, 'file')}</dd>
                  <dt className="text-casevo-text-soft">Failed</dt><dd className={`m-0 mb-[4px] sm:mb-0 tabular-nums ${backup.lastRun.failed > 0 ? 'text-casevo-danger font-semibold' : 'text-casevo-forest'}`}>{plural(backup.lastRun.failed, 'file')}</dd>
                  <dt className="text-casevo-text-soft">Still waiting</dt><dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest tabular-nums">{plural(backup.backlog, 'file')}</dd>
                  {backup.lastRun.error && <><dt className="text-casevo-text-soft">Error</dt><dd className="m-0 text-casevo-danger break-words">{backup.lastRun.error}</dd></>}
                </dl>
              )}
            </ActionBox>
          ) : evidence.backupConfigured ? (
            <ActionBox variant="slim" tone="done" title="Evidence backup" status="· locked S3 copy configured"
              message={`Last backup ${evidence.lastBackupAt ? ukDateTime(evidence.lastBackupAt) : 'not yet'} · ${evidence.pendingBackups === 0 ? 'nothing waiting' : `${evidence.pendingBackups} waiting to be copied`}.`} />
          ) : (
            <ActionBox tone="danger" title="Evidence backup not set up"
              message="Signed evidence is only in R2 until the locked S3 copy is set up. This must be fixed before go-live." />
          )}
          {/* (D12) The retention purge's status. */}
          {purge && pv && (
            <div data-purge-status={pv.tone}>
              <ActionBox tone={pv.tone} title={pv.title} message={pv.message} ariaLabel={pv.title}>
                {purge.lastRun && (
                  <dl className="m-0 mt-[8px] grid grid-cols-1 sm:grid-cols-[130px_minmax(0,1fr)] gap-x-[12px] gap-y-[2px] sm:gap-y-[4px] text-[12.5px]" data-purge-run="">
                    <dt className="text-casevo-text-soft">Last run</dt>
                    <dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest">{ukDateTime(purge.lastRun.startedAt)}{purge.lastRun.finishedAt ? '' : ' (still running)'}</dd>
                    <dt className="text-casevo-text-soft">Purged</dt><dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest tabular-nums">{plural(purge.lastRun.purged, 'record')}</dd>
                    <dt className="text-casevo-text-soft">Failed</dt><dd className={`m-0 mb-[4px] sm:mb-0 tabular-nums ${purge.lastRun.failed > 0 ? 'text-casevo-danger font-semibold' : 'text-casevo-forest'}`}>{plural(purge.lastRun.failed, 'record')}</dd>
                    <dt className="text-casevo-text-soft">Due now</dt><dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest tabular-nums">{plural(purge.due, 'record')}</dd>
                    {purge.waitingForLock > 0 && <><dt className="text-casevo-text-soft">Waiting for lock</dt><dd className="m-0 mb-[4px] sm:mb-0 text-casevo-forest tabular-nums">{plural(purge.waitingForLock, 'record')}</dd></>}
                    {purge.lastRun.error && <><dt className="text-casevo-text-soft">Error</dt><dd className="m-0 text-casevo-danger break-words">{purge.lastRun.error}</dd></>}
                  </dl>
                )}
              </ActionBox>
            </div>
          )}
          {purge === null && <div data-purge-status="unknown"><ActionBox variant="slim" tone="warn" title="Retention purge" status="· status couldn’t be read just now" /></div>}
          {data.status.sms.configured
            ? <ActionBox variant="slim" tone="done" title="Text messages" status={`· ${data.status.sms.provider === 'fake' ? 'TEST mode — codes go to the server log, nothing is texted' : 'connected'}`} />
            : <ActionBox tone="danger" title="Text messages not set up" message="Signing codes can’t be texted until SMS_PROVIDER and SMSWORKS_API_TOKEN are set on the server." />}
          {evidence.failedChecks > 0 && (
            <ActionBox tone="danger" title={`${evidence.failedChecks} integrity check${evidence.failedChecks === 1 ? '' : 's'} failed`}
              message={`A stored evidence file no longer matches its fingerprint${evidence.lastCheckAt ? ` (last check ${ukDateTime(evidence.lastCheckAt)})` : ''}. It has been reported — contact Casevo support.`} />
          )}
        </div>
      </SettingsCard>

      <ConfirmDialogue isOpen={confirmPilotOff} title="Switch e-signature off?"
        message={PILOT_OFF_WARNING}
        confirmLabel="Switch it off" tone="danger" onCancel={() => setConfirmPilotOff(false)} onConfirm={() => { setConfirmPilotOff(false); void patch('pilot', { pilotEnabled: false }, {}) }} />
      <ConfirmDialogue isOpen={confirmWithdraw} title="Withdraw approval of the consent wording?"
        message="The wording goes back to DRAFT. Documents can’t be sent for e-signature until a solicitor at your firm approves it again."
        confirmLabel="Withdraw approval" tone="danger" onCancel={() => setConfirmWithdraw(false)} onConfirm={() => { setConfirmWithdraw(false); void approve(false) }} />
      <ConfirmDialogue isOpen={confirmExplainerWithdraw} title="Withdraw approval of the statement?"
        message="“How Casevo e-signatures work” goes back to DRAFT, and new evidence packs will mark it so."
        confirmLabel="Withdraw approval" tone="danger" onCancel={() => setConfirmExplainerWithdraw(false)} onConfirm={() => { setConfirmExplainerWithdraw(false); void approveExplainer(false) }} />
    </div>
  )
}

export default EsignSettingsPanel
